import { initializeApp, getApps, type FirebaseApp } from 'firebase/app'
import * as AuthModule from 'firebase/auth'
import type { Auth } from 'firebase/auth'
import { 
  initializeFirestore,
  getFirestore,
  connectFirestoreEmulator,
  type Firestore
} from 'firebase/firestore'
import { 
  getStorage,
  type FirebaseStorage
} from 'firebase/storage'

// Validate Firebase environment variables for common issues
const validateFirebaseConfig = () => {
  const config = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
  }

  // Check for newline characters in environment variables
  Object.entries(config).forEach(([key, value]) => {
    if (value && (value.includes('\n') || value.includes('\r'))) {
      console.error(`❌ [Firebase] Environment variable ${key} contains newline characters. This will cause authentication issues.`)
      console.error(`❌ [Firebase] Value: ${JSON.stringify(value)}`)
    }
  })

  // Check for missing critical variables in production
  if (import.meta.env.PROD) {
    const criticalVars = ['VITE_FIREBASE_API_KEY', 'VITE_FIREBASE_AUTH_DOMAIN', 'VITE_FIREBASE_PROJECT_ID']
    criticalVars.forEach(varName => {
      if (!import.meta.env[varName]) {
        console.error(`❌ [Firebase] Missing critical environment variable in production: ${varName}`)
      }
    })
  }
}

// Run validation
validateFirebaseConfig()

// Firebase configuration
const isDevelopment = import.meta.env.DEV || import.meta.env.MODE === 'development'
const useEmulatorInDev = isDevelopment && import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true'

const resolveFirebaseApiKey = (): string => {
  const apiKey = import.meta.env.VITE_FIREBASE_API_KEY
  if (apiKey) return apiKey
  if (import.meta.env.PROD) {
    throw new Error('VITE_FIREBASE_API_KEY is required in production builds')
  }
  return 'demo-api-key'
}

const firebaseConfig = {
  apiKey: resolveFirebaseApiKey(),
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "demo-livewello-v4.firebaseapp.com",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "demo-livewello-v4",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "livewello-v4-data-bucket",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "123456789",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "1:123456789:web:demo-app-id",
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
}

// Initialize Firebase app
const existingApps = getApps()
const app: FirebaseApp = existingApps.length > 0 ? existingApps[0] : initializeApp(firebaseConfig)

// Initialize Auth using getAuth() for better production compatibility
let auth: Auth
try {
  // Use getAuth() instead of initializeAuth() to avoid "Component auth has not been registered yet" errors
  auth = AuthModule.getAuth(app)
  
  // Set persistence for both dev and prod to ensure auth state is available across windows
  // This is critical for "View as" functionality where admin opens target user in new window
  AuthModule.setPersistence(auth, AuthModule.browserLocalPersistence)
} catch (e) {
  console.error('❌ [Firebase] Failed to initialize Auth:', e)
  throw e
}

// View-As Support: Create a separate Firebase app instance for admin impersonation
// This allows view-as tabs to have their own auth state without affecting the admin tab
let viewAsApp: FirebaseApp | null = null
let viewAsAuth: Auth | null = null

export function getViewAsApp(): FirebaseApp {
  if (!viewAsApp) {
    const existingViewAsApp = getApps().find(app => app.name === 'view-as')
    if (existingViewAsApp) {
      viewAsApp = existingViewAsApp
    } else {
      viewAsApp = initializeApp(firebaseConfig, 'view-as')
    }
  }
  return viewAsApp
}

export function getViewAsAuth(): Auth {
  if (!viewAsAuth) {
    const app = getViewAsApp()
    viewAsAuth = AuthModule.getAuth(app)
    // Set persistence for both dev and prod
    AuthModule.setPersistence(viewAsAuth, AuthModule.browserLocalPersistence)
    
    // Connect view-as auth to emulator if in development
    if (isDevelopment && useEmulatorInDev) {
      try {
        AuthModule.connectAuthEmulator(viewAsAuth, 'http://127.0.0.1:9199', { disableWarnings: true })
        console.log('✅ [Firebase] View-as auth connected to emulator')
      } catch (e) {
        console.warn('⚠️ [Firebase] View-as auth emulator connection failed or already connected:', e)
      }
    }
  }
  return viewAsAuth
}

// Get view-as Firestore instance (uses view-as app, so auth matches)
let viewAsDb: Firestore | null = null
export function getViewAsDb(): Firestore {
  if (!viewAsDb) {
    const app = getViewAsApp()
    try {
      viewAsDb = getFirestore(app, databaseId !== '(default)' ? databaseId : undefined)
    } catch (error) {
      viewAsDb = initializeFirestore(app, { 
        ...firestoreSettings,
        ...(databaseId !== '(default)' && { databaseId })
      })
    }
    
    // Connect to emulator if in development
    if (isDevelopment && useEmulatorInDev) {
      try {
        connectFirestoreEmulator(viewAsDb, '127.0.0.1', 8180)
        console.log('✅ [Firebase] View-as Firestore connected to emulator')
      } catch (e) {
        console.warn('⚠️ [Firebase] View-as Firestore emulator connection failed or already connected:', e)
      }
    }
  }
  return viewAsDb
}

// Get active database instance based on view-as mode
export function getActiveDb(): Firestore {
  return isViewAsMode() ? getViewAsDb() : db
}

// Helper to determine if we're in view-as mode (check sessionStorage)
export function isViewAsMode(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return sessionStorage.getItem('viewAsMode') === 'true'
  } catch (e) {
    // sessionStorage might not be available in some contexts
    return false
  }
}

// Get the appropriate auth instance based on current mode
export function getActiveAuth(): Auth {
  try {
    return isViewAsMode() ? getViewAsAuth() : auth
  } catch (e) {
    // If there's any issue getting view-as auth, fall back to default
    console.warn('Failed to get active auth, falling back to default:', e)
    return auth
  }
}

// Determine which database to use based on environment
const databaseId = import.meta.env.VITE_FIREBASE_DATABASE_ID || '(default)'

// Validate database configuration
const validateDatabaseConfig = () => {
  if (import.meta.env.PROD && databaseId !== '(default)') {
    console.warn(`⚠️ [Firebase] Production environment using non-default database: ${databaseId}`)
    console.warn('⚠️ [Firebase] This may cause Listen channel issues. Consider using (default) database.')
  }
  
  if (import.meta.env.DEV && databaseId === '(default)') {
    console.warn('⚠️ [Firebase] Development environment using default database. This may conflict with production.')
  }
  
  console.log(`📊 [Firebase] Using database: ${databaseId}`)
}

// Run database validation
validateDatabaseConfig()

// Production-specific Firestore settings to fix Listen channel hanging issues
const isProduction = import.meta.env.PROD
const firestoreSettings = isProduction ? {
  // Production: Force long polling to avoid QUIC protocol errors (ERR_QUIC_PROTOCOL_ERROR.QUIC_TOO_MANY_RTOS)
  // QUIC can fail due to network issues, firewalls, or browser extensions
  experimentalAutoDetectLongPolling: false,
  experimentalForceLongPolling: true,
  // Note: experimentalConnectTimeout is NOT a valid setting in Firebase SDK 11.x
  // Removing it to fix 400 Bad Request errors on Listen channel
} : {
  // Development: Disable auto-detection for emulator compatibility
  experimentalAutoDetectLongPolling: false,
}

let db: Firestore
try {
  // Try to get existing instance first
  db = getFirestore(app, databaseId !== '(default)' ? databaseId : undefined)
} catch (error) {
  // If no existing instance, initialize with environment-specific options
  db = initializeFirestore(app, { 
    ...firestoreSettings,
    ...(databaseId !== '(default)' && { databaseId })
  })
}

let storage: FirebaseStorage
try {
  storage = getStorage(app)
} catch (error) {
  console.error('❌ [Firebase] Failed to initialize Storage:', error)
  throw error
}

// Connect to emulators if in development
if (isDevelopment && useEmulatorInDev) {
  try {
    AuthModule.connectAuthEmulator(auth, 'http://127.0.0.1:9199', { disableWarnings: true })
  } catch (e) {
    console.warn('⚠️ [Firebase] Auth emulator connection failed or already connected:', e)
  }
  try {
    connectFirestoreEmulator(db, '127.0.0.1', 8180)
  } catch (e) {
    console.warn('⚠️ [Firebase] Firestore emulator connection failed or already connected:', e)
  }
}

export const waitForFirebase = async () => Promise.resolve()

// Google Auth Provider
export const googleProvider = new AuthModule.GoogleAuthProvider()
googleProvider.addScope('email')
googleProvider.addScope('profile')
googleProvider.setCustomParameters({
  prompt: 'select_account'
})

// Export services as concrete instances
export { auth, db, storage }
export default app

// For backward compatibility
export const ensureInitialized = waitForFirebase
export const firebaseReady = waitForFirebase()

// Legacy exports for compatibility
export const getFirebaseAuth = async () => auth
export const getFirebaseDb = async () => db
export const getFirebaseStorage = async () => storage

export const getAuthSync = () => auth
export const getDbSync = () => db
export const getStorageSync = () => storage

// Debug helpers
export const debugFirebase = () => {
  console.log('Firebase status:', {
    app: !!app,
    auth: !!auth,
    db: !!db,
    storage: !!storage,
  })
}

export const debugFirebaseConnection = async () => {
  debugFirebase()
}

// Connection health monitoring and retry utilities
export class FirestoreConnectionManager {
  private static failures = 0
  private static lastFailureTime = 0
  private static readonly threshold = 3  // Reduced from 5 for faster failure detection
  private static readonly timeout = 30000 // Reduced from 60000 (30 seconds instead of 1 minute)

  static async executeWithRetry<T>(
    operation: () => Promise<T>, 
    maxRetries = 3,
    baseDelay = 1000
  ): Promise<T> {
    for (let i = 0; i < maxRetries; i++) {
      try {
        const result = await operation()
        this.onSuccess()
        return result
      } catch (error) {
        if (i === maxRetries - 1) {
          this.onFailure()
          throw error
        }
        
        // Exponential backoff with jitter
        const delay = baseDelay * Math.pow(2, i) + Math.random() * 1000
        console.warn(`Firestore operation failed, retrying in ${delay}ms (attempt ${i + 1}/${maxRetries}):`, error)
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }
    throw new Error('Max retries exceeded')
  }

  static isCircuitOpen(): boolean {
    return this.failures >= this.threshold && 
           (Date.now() - this.lastFailureTime) < this.timeout
  }

  private static onSuccess(): void {
    this.failures = 0
  }

  private static onFailure(): void {
    this.failures++
    this.lastFailureTime = Date.now()
  }
}

// Connection health monitoring
export const setupConnectionMonitoring = () => {
  if (typeof window !== 'undefined') {
    window.addEventListener('online', () => {
      console.log('🟢 Network online - Firestore should reconnect')
    })
    
    window.addEventListener('offline', () => {
      console.log('🔴 Network offline - Firestore connections may hang')
    })

    // Monitor for unhandled promise rejections related to Firestore
    window.addEventListener('unhandledrejection', (event) => {
      if (event.reason && event.reason.message && 
          event.reason.message.includes('firestore') && 
          event.reason.message.includes('400')) {
        console.warn('Unhandled Firestore Listen channel error:', event.reason.message)
        event.preventDefault() // Prevent the error from being logged to console
      }
    })
  }
}

// Global error handler for Firestore Listen channel errors
export const setupFirestoreErrorHandling = () => {
  // Listen for unhandled Firestore errors
  if (typeof window !== 'undefined') {
    window.addEventListener('error', (event) => {
      const errorMessage = event.error?.message || event.message || ''
      const isFirestoreError = errorMessage.includes('400') || 
                               errorMessage.includes('QUIC') || 
                               errorMessage.includes('firestore') ||
                               event.filename?.includes('firestore')
      
      if (isFirestoreError) {
        console.warn('Global Firestore error detected:', errorMessage)
        
        // Log additional context for debugging
        console.warn('Firestore error context:', {
          url: event.filename,
          line: event.lineno,
          column: event.colno,
          stack: event.error?.stack,
          timestamp: new Date().toISOString()
        })
        
        // Check if circuit breaker is open
        if (FirestoreConnectionManager.isCircuitOpen()) {
          console.warn('Firestore circuit breaker is open - temporarily disabling real-time features')
        } else {
          console.warn('Firestore circuit breaker is closed - errors will be retried')
        }
      }
    })

    // Enhanced unhandled promise rejection handling
    window.addEventListener('unhandledrejection', (event) => {
      const errorMessage = event.reason?.message || String(event.reason) || ''
      const isFirestoreError = errorMessage.includes('firestore') || 
                               errorMessage.includes('400') ||
                               errorMessage.includes('QUIC')
      
      if (isFirestoreError) {
        console.warn('Unhandled Firestore error:', errorMessage)
        console.warn('Promise rejection context:', {
          reason: event.reason,
          timestamp: new Date().toISOString()
        })
        event.preventDefault() // Prevent the error from being logged to console
      }
    })
  }
}

// Initialize error handling and monitoring
setupFirestoreErrorHandling()
setupConnectionMonitoring()